import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import CustomTabContainer from "../../../../components/CustomTabContainer";
import CustomTab from "../../../../components/CustomTab";
import ButtonAction from "../../../../components/buttons/ButtonAction";
import {TrashOutlineIcon} from "../../../../components/buttons/IconButtons";
import CustomAssignedItems from "../../../../components/CustomAvailableAssignedItems/CustomAssignedItems";
import {SEARCH_MODEL} from "../../../../components/table/Model/TableModel";
import {SimpleSearch} from "../../../../reduxToolkit/Search/SearchApi";
import {VIEW_TYPE} from "../../../../enums/ViewType";
import DeleteModal from "../../../Modals/DeleteModal";
import {ZONE_ACCESS_ITEM_TABS} from "../Model/Model";

/**
 * Sub-tabs of the zones or the guards on the update of a zone access: one to add new items and one
 * with the items already assigned, paged and searched on the service over one list shared by both,
 * as the devices TabPanel does. Assigned items are checked and removed with the delete button, or one
 * by one with their close icon, after confirming it.
 *
 * @param {string} id Prefix of the ids of the elements.
 * @param {string} zoneAccessId Zone access being updated.
 * @param {Object} addTab Tab to add items, with its `title` and `icon`.
 * @param {Object} assignedTab Tab of the assigned items, with its `title` and `icon`.
 * @param {ReactNode} addContent Content shown in the add tab.
 * @param {Object} assignedList List of the assigned items: its `model`, `keyId`, `searchTitle`,
 *        `labelRowsPerPage`, `notInformationLegend` and `renderAssignedItem`, see CustomAssignedItems.
 * @param {Array} assigned Items assigned, the list is read again whenever they change and the
 *        assigned tab shows how many they are.
 * @param {boolean} canDelete Whether the assigned items can be removed.
 * @param {Function} onRemove Removes the given item ids, calls the given callback once removed.
 * @param {string} removeTitle Title of the confirmation to remove.
 * @param {string} removeElement Name of the removed items in the confirmation.
 * @param {Object} results, setResults, searchElement, setSearchElement, setSearch, setSimpleSearch,
 *        setBodyForExport, modified, setModified List shared by both tabs, as the devices do: the
 *        add content reads it too, so only the tab shown reads it and a tab change starts it over.
 * @param setResults
 * @param searchElement
 * @param setSearchElement
 * @param setSearch
 * @param setSimpleSearch
 * @param setBodyForExport
 * @param modified
 * @param setModified
 * @param {boolean} disabled Read-only, only the assigned items are shown.
 */
const ZoneAccessItemTabs = ({
                                id,
                                zoneAccessId,
                                addTab,
                                assignedTab,
                                addContent,
                                assignedList,
                                assigned,
                                canDelete,
                                onRemove,
                                removeTitle,
                                removeElement,
                                results,
                                setResults,
                                searchElement,
                                setSearchElement,
                                setSearch,
                                setSimpleSearch,
                                setBodyForExport,
                                modified,
                                setModified,
                                disabled = false
                            }) => {
    const {t} = useTranslation();
    const [activeTab, setActiveTab] = useState(disabled ? ZONE_ACCESS_ITEM_TABS.ASSIGNED : ZONE_ACCESS_ITEM_TABS.ADD);
    const [checkedIds, setCheckedIds] = useState([]);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [deleteModalShow, setDeleteModalShow] = useState(false);
    const isMounted = useRef(false);

    const {keyId, model} = assignedList;

    // "De/select all" follows the selection, also when an item is picked from its close icon
    useEffect(() => {
        const content = results?.content || [];
        setIsAllChecked(content.length > 0 && content.every((item) => checkedIds.includes(item[keyId])));
    }, [checkedIds, results]);

    // Assigned items changed by an update or a removal, the tab shown reads its page again
    useEffect(() => {
        if (isMounted.current) setModified(true);
        else isMounted.current = true;
    }, [assigned]);

    // Read-only, nothing can be added. The assigned tab shows how many items the zone access has
    const tabs = [
        !disabled && {key: ZONE_ACCESS_ITEM_TABS.ADD, ...addTab},
        {key: ZONE_ACCESS_ITEM_TABS.ASSIGNED, ...assignedTab, count: assigned?.length ?? 0},
    ].filter(Boolean);

    /**
     * Request of a page of the assigned items, searched by the typed text.
     */
    const tableBuildBody = ({page, rowsPerPage, searchElement}) => ({
        body: SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            model,
            key: searchElement,
            modelId: zoneAccessId,
        }),
    });

    /**
     * Asks to remove a single item from its close icon.
     */
    const handleDelete = (item) => {
        setCheckedIds([item[keyId]]);
        setDeleteModalShow(true);
    };

    const hide = () => {
        setCheckedIds([]);
        setIsAllChecked(false);
        setDeleteModalShow(false);
    };

    return (
        <div className={"border-full-shades-200 col-12 border-radius-8px px-0"}>
            <CustomTabContainer
                activeTab={activeTab}
                setActiveTab={(tab) => {
                    // Both tabs share the list, the tab shown reads it from the start
                    setSearchElement('');
                    setResults(null);
                    hide();
                    setActiveTab(tab);
                }}
                className="border-0 px-3 d-flex justify-content-between"
                paddingClass=''
                buttonAction={
                    canDelete && activeTab === ZONE_ACCESS_ITEM_TABS.ASSIGNED &&
                    <ButtonAction
                        id={`btn_${id}_delete`}
                        name='delete'
                        className={'btn-icon-delete h-100'}
                        Icon={TrashOutlineIcon}
                        disabled={checkedIds.length <= 0}
                        onClick={() => setDeleteModalShow(true)}
                    />
                }
            >
                {tabs.map((tab) => (
                    <CustomTab
                        key={tab.key}
                        id={tab.key}
                        title={t(tab.title)}
                        newsNumber={tab.count}
                        icon={tab.icon}
                        selected={activeTab === tab.key}
                        onClick={() => setActiveTab(tab.key)}
                    />
                ))}
            </CustomTabContainer>

            <div className="border-bottom-shades-300 mb-3"/>

            <div className="px-3">
                {activeTab === ZONE_ACCESS_ITEM_TABS.ADD
                    ? addContent
                    : <CustomAssignedItems
                        setBodyForExport={setBodyForExport}
                        results={results}
                        setResults={setResults}
                        searchElement={searchElement}
                        setSearchElement={setSearchElement}
                        setSearch={setSearch}
                        setSimpleSearch={setSimpleSearch}
                        modified={modified}
                        setModified={setModified}
                        selected={checkedIds}
                        setSelected={setCheckedIds}
                        isAllChecked={isAllChecked}
                        setIsAllChecked={setIsAllChecked}
                        labelRowsPerPage={assignedList.labelRowsPerPage}
                        tableNotInformationLegend={assignedList.notInformationLegend}
                        tableBuildBody={tableBuildBody}
                        tableEndPoint={SimpleSearch}
                        keyId={keyId}
                        viewTypeName={VIEW_TYPE.GRID.name}
                        onDelete={canDelete ? handleDelete : undefined}
                        checkbox={canDelete}
                        searchTitle={assignedList.searchTitle}
                        renderAssignedItem={assignedList.renderAssignedItem}
                        // Same spacing as the add tab: no top padding under the tab bar
                        containerClass={'row pb-3'}
                    />
                }
            </div>

            {deleteModalShow &&
                <DeleteModal
                    show={deleteModalShow}
                    onHide={hide}
                    onClickFn={() => onRemove(checkedIds, hide)}
                    data={checkedIds}
                    title_modal={t(removeTitle)}
                    description={'are_you_sure_you_want_to_remove'}
                    element_modal={t(removeElement)}
                    buttonCancelText={'cancel'}
                />
            }
        </div>
    );
};

export default ZoneAccessItemTabs;
